import { graphql as gql } from "@/generated";
import type {
  AuthorizationRule,
  AuthorizationRuleGroup,
} from "@/generated/graphql";
import type { NullableGqlFragmentType } from "@/utils/types";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { injectLocal, provideLocal } from "@vueuse/core";
import {
  computed,
  type InjectionKey,
  type MaybeRefOrGetter,
  type UnwrapNestedRefs,
} from "vue";
import { useGatingRequestedPermissions } from "./evaluation/use-gating-requested-permissions";

const gatingPermissionsOverrideInjectionKey = Symbol(
  "gatingPermissions",
) as InjectionKey<ReturnType<typeof useProvideGatingPermissions>>;

const _AuthorizationRuleForGatingFragment = gql(/* GraphQL */ `
  fragment AuthorizationRuleForGating on AuthorizationRule {
    id
    significant_permissions_mask
    channel_membership {
      id
      name
      price_tag
      permission_mask
      permission_mask_level
    }
    web_three_token_criterion {
      id
      min_tokens
      web_three_erc_twenty_token {
        address
        label
        symbol
        external_partner_id
        external_partner
      }
      permission_mask
      permission_mask_level
    }
    points_criterion {
      id
      min_points
      time_window
      community_id
      community_name
      user_current_points
    }
    product_purchase_criterion {
      id
      operator
      required_products {
        id
        product {
          id
          name
          product_type
          is_active
          action_label
        }
      }
      user_meets_requirements
    }
  }
`);

export const AuthorizationRuleGroupForGatingFragment = gql(/* GraphQL */ `
  fragment AuthorizationRuleGroupForGating on AuthorizationRuleGroup {
    id

    authorization_rules {
      ...AuthorizationRuleForGating
    }
  }
`);

export type AuthorizationRuleForGating = ResultOf<
  typeof _AuthorizationRuleForGatingFragment
>;

export type AuthorizationRuleForGatingType =
  | NullableGqlFragmentType<typeof _AuthorizationRuleForGatingFragment>
  | AuthorizationRule;

export type AuthorizationRuleGroupForGating = {
  id: string | undefined | null;
  authorization_rules: AuthorizationRuleForGating[];
};

export type AuthorizationRuleGroupForGatingType =
  | NullableGqlFragmentType<typeof AuthorizationRuleGroupForGatingFragment>
  | AuthorizationRuleGroup;

export type UnnormalizedAuthorizationRuleGroupForGatingType = MaybeRefOrGetter<
  UnwrapNestedRefs<AuthorizationRuleGroupForGatingType> | null | undefined
>;

export function useProvideGatingPermissions(
  unnormalizedGroup: UnnormalizedAuthorizationRuleGroupForGatingType,
) {
  const { authorizationRuleGroup, requestedPermissions } =
    useGatingRequestedPermissions(unnormalizedGroup);

  // Note: Previously this called identityStore.service.handleAuthorizationRuleGroup()
  // for pre-emptive token balance syncing. This is now handled by the WalletService
  // when wallet capabilities are enabled.

  const definition = {
    requestedPermissions,
    authorizationRuleGroup,
  };

  provideLocal(gatingPermissionsOverrideInjectionKey, definition);

  return definition;
}

export function useGatingPermissions(): ReturnType<
  typeof useProvideGatingPermissions
> {
  const value = injectLocal(gatingPermissionsOverrideInjectionKey, {
    requestedPermissions: computed(() => ({
      permission_mask: 0,
      permission_mask_level: 0,
    })),
    authorizationRuleGroup: computed(() => null),
  } as const);

  return value;
}
